/* eslint-disable react-hooks/exhaustive-deps */
import { ASSIGN_UPDATE_ORDER_BY_STATUS, EXPORT_ORDER_TYPE, ITEM_DATA_TYPE } from "features/Order/constants";
import { useCloneCart, useUpdateCartV2 } from "hooks/cart";
import { useKeyManagementPermissions } from "hooks/keyManagement/query";
import {
  useApproveOrder,
  useApproveStock,
  useCancelOrder,
  useChangeWarehouse,
  useCloneCartUpdateDetail,
  useCompleteAssembly,
  useConfirmD3P,
  useConfirmDeliver,
  useConfirmUpdateDetail,
  useDenyStock,
  useExportOrder,
  useGetOrderDetail,
  useOrderPermissions,
  usePackageOrder,
  useWaitingStock,
} from "hooks/order";
import { t } from "i18next";
import { pick } from "lodash";
import printJS from "print-js";
import { useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { notify } from "utils/helperFuncs";

const useDetail = () => {
  const { id } = useParams();
  const { handleExportOrder, loading: loadingExport } = useExportOrder();

  const { canPromotionOrderView } = useOrderPermissions();

  const params = useMemo(() => {
    if (canPromotionOrderView === undefined) return null;
    return {
      id: id,
      itemDataType: canPromotionOrderView ? ITEM_DATA_TYPE.COMBO_ORDER_ITEM : ITEM_DATA_TYPE.ORDER_ITEM,
    };
  }, [canPromotionOrderView]);

  const { order, loading: loadingOrder, refetch: refetchOrder } = useGetOrderDetail({ params });
  const { handleApproveOrder, loading: loadingApproveOrder } = useApproveOrder();
  const { handleCancelOrder, loading: loadingCancelOrder } = useCancelOrder();
  const { handleWaitingStock, loading: loadingWaitingStock } = useWaitingStock();
  const { handleDenyStock, loading: loadingDenyStock } = useDenyStock();
  const { handleApproveStock, loading: loadingApproveStock } = useApproveStock();
  const { handlePackageOrder, loading: loadingPackageOrder } = usePackageOrder();
  const { handleCompleteAssembly, loading: loadingCompleteAssembly } = useCompleteAssembly();
  const { handleConfirmD3P, loading: loadingConfirmD3P } = useConfirmD3P();
  const { handleConfirmDeliver, loading: loadingConfirmShipper } = useConfirmDeliver();
  const { handleCloneCart, loading: loadingCloneCart } = useCloneCart();
  const {
    canApproveStock,
    canAssignShipper,
    canCancelOrder,
    canCompleteAssembly,
    canCompleteOrder,
    canConfirmPayment,
    canDenyStock,
    canExportStock,
    canRecreateCart,
    canViewOrder,
    canWaitStock,
    canApproveOrder,
    canOrderCancelBeforePayView,
    canOrderCancelAfterPayView,
    loading: loadingPermission,
  } = useOrderPermissions();

  const { canSuggestKeyLicenseAccountConfirm, canSuggestKeyLicenseAccountCancel } = useKeyManagementPermissions();
  const { handleCloneCartUpdateDetail, loading: loadingCloneCartUpdateDetail } = useCloneCartUpdateDetail();
  const { handleChangeWarehouse, loading: loadingChangeWarehouse } = useChangeWarehouse();
  const { handleUpdateCartV2 } = useUpdateCartV2();
  const { handleConfirmUpdateDetail } = useConfirmUpdateDetail();
  const [loadingApplyCashPaymentMethod, setLoadingApplyCashPaymentMethod] = useState(false);

  const history = useHistory();

  const onApplyCashPaymentMethod = async () => {
    try {
      setLoadingApplyCashPaymentMethod(true);
      const cartResponse = await handleCloneCartUpdateDetail({ orderID: id });
      const cartID = cartResponse?.data?.order?.cloneCartUpdateDetail?.id;

      if (!cartID) {
        throw new Error(t("common.error"));
      }

      await handleUpdateCartV2({
        cartID,
        request: {
          paymentMethod: "CASH",
        },
      });

      await handleConfirmUpdateDetail({ orderID: id });
      await refetchOrder();

      notify.success({
        message: "Áp dụng phương thức thanh toán thành công",
      });
    } catch (error) {
      notify.error({
        message: "Áp dụng phương thức thanh toán thất bại",
        description: error?.message,
      });
    } finally {
      setLoadingApplyCashPaymentMethod(false);
    }
  };

  const onExportOrder = async (type) => {
    await handleExportOrder({ orderID: id, fileType: type })
      .then((response) => {
        const fileUrl = response.data.order.exportFile.url;
        window.open(fileUrl, "_blank").focus();
      })
      .catch(() => {
        notify.error({ message: t("common.error") });
      });
  };

  const onPrintOrder = async () => {
    await handleExportOrder({
      orderID: id,
      fileType: EXPORT_ORDER_TYPE.PDF,
    })
      .then((response) => {
        const fileUrl = response.data.order.exportFile.url;
        printJS({
          printable: fileUrl,
          type: EXPORT_ORDER_TYPE.PDF.toLowerCase(),
          showModal: true,
          modalMessage: t("common.loadingData"),
        });
      })
      .catch((error) => {
        notify.error({
          message: t("order.orderDetail.exportOrderError"),
          description: error.message,
        });
      });
  };

  const onCancelOrder = async (reason) => {
    if (reason) {
      await handleCancelOrder({ orderIDs: [id], reason })
        .then(() => {
          notify.success({
            message: t("order.cancelOrder.cancelOrderSuccess"),
          });
        })
        .catch((error) => {
          notify.error({
            message: t("order.cancelOrder.cancelOrderError"),
            description: error.message,
          });
        });
    } else {
      notify.warning({
        message: t("order.cancelOrder.warningMessage2"),
      });
    }
  };

  const onEditOrder = async () => {
    await handleCloneCartUpdateDetail({ orderID: id })
      .then((res) => {
        const cloneCartID = res?.data?.order?.cloneCartUpdateDetail?.id;
        // BE có thể trả thành công nhưng không kèm `id` (hoặc lỗi bị nuốt) — thiếu guard này sẽ
        // điều hướng sang `/order-edit/undefined`, khiến `GetCart` ở màn giỏ hàng gọi với `id: null`
        // và BE trả lỗi validation "cannot be null".
        if (!cloneCartID) {
          notify.error({ message: t("order.orderDetail.editOrderError") });
          return;
        }
        history.push({
          pathname: `/order-edit/${cloneCartID}`,
          search: `?orderID=${id}`,
        });
      })
      .catch((error) => {
        notify.error({
          message: error.message,
        });
      });
  };

  const onWaitingForEnoughStock = async () => {
    await handleWaitingStock({ orderIDs: [id] })
      .then(() => {
        notify.success({
          message: t("order.orderList.waitingForStockSuccess"),
        });
      })
      .catch((error) => {
        notify.error({
          message: t("order.orderList.waitingForStockError"),
          description: error.message,
        });
      });
  };

  const onDeny = async () => {
    await handleDenyStock({ orderIDs: [id] })
      .then(() => {
        notify.success({
          message: t("order.orderList.denyStockSuccess"),
        });
      })
      .catch((error) => {
        notify.error({
          message: t("order.orderList.denyStockError"),
          description: error.message,
        });
      });
  };

  const onApproveStock = async () => {
    await handleApproveStock({ orderIDs: [id] })
      .then(() => {
        notify.success({
          message: t("order.orderList.confirmStockSuccess"),
        });
      })
      .catch((error) => {
        notify.error({
          message: t("order.orderList.confirmStockError"),
          description: error.message,
        });
      });
  };

  const onExportOrderWithPrint = async (handlePrintDialogClose) => {
    await handleExportOrder({ orderID: id, fileType: EXPORT_ORDER_TYPE.PDF })
      .then((response) => {
        const fileUrl = response.data.order.exportFile.url;
        printJS({
          printable: fileUrl,
          type: EXPORT_ORDER_TYPE.PDF.toLowerCase(),
          showModal: true,
          modalMessage: t("common.loadingData"),
          onPrintDialogClose: handlePrintDialogClose,
        });
      })
      .catch(() => {
        notify.error({ message: t("common.error") });
      });
  };

  const onPackage = async () => {
    await handlePackageOrder({ orderIDs: [id] })
      .then(() => {
        notify.success({
          message: t("order.orderList.packageSuccess"),
        });
      })
      .catch((error) => {
        notify.error({
          message: t("order.orderList.packageError"),
        });
      });
    // await handleCancelOrder();
  };

  const onExportConfirm = async () => {
    const url = "/export-serial/create";
    const data = {
      orderInfoState: order,
    };
    const newTab = window.open();
    newTab.location.href = url;
    newTab.orderInfoState = data;
  };

  const onAssemblyConfirm = async () => {
    await handleCompleteAssembly({ orderIDs: [id] })
      .then(() => {
        notify.success({
          message: t("order.orderList.confirmAssembleSuccess"),
        });
      })
      .catch((error) => {
        notify.error({
          message: t("order.orderList.confirmAssembleError"),
          description: error.message,
        });
      });
  };

  const onConfirmShipper = async (shipperID) => {
    await handleConfirmDeliver({
      orderIDs: [id],
      shipperID,
    })
      .then(() => {
        notify.success({
          message: t("order.selectingShipper.confirmDeliverSuccess"),
        });
      })
      .catch((error) => {
        notify.error({
          message: t("order.selectingShipper.confirmDeliverError"),
          description: error.message,
        });
      });
  };

  const onConfirmD3P = async () => {
    await handleConfirmD3P({
      orderIDs: [id],
    })
      .then(() => {
        notify.success({
          message: t("order.orderList.confirm3PDeliverySuccess"),
        });
      })
      .catch((error) => {
        notify.error({
          message: t("order.orderList.confirm3PDeliveryError"),
          description: error.message,
        });
      });
  };

  const onRecreateOrder = async () => {
    await handleCloneCart(order?.cartID)
      .then((response) => {
        const id = response?.data?.cart?.clone?.id;
        history.push({
          pathname: "/cart",
          search: `?id=${id}`,
        });
        notify.success({
          message: t("order.orderDetail.recreateCartSuccess"),
        });
      })
      .catch((error) => {
        notify.error({
          message: t("order.orderDetail.recreateCartError"),
          description: error?.message,
        });
      });
  };

  const onApproveOrder = async ({
    warehouseID,
    paymentMethod,
    deadlineDelivery,
    internalNote,
    pickupWarehouseID,
    pickupReceiverID,
  }) =>
    await handleApproveOrder({
      orderIDs: [id],
      updateWarehouseID: warehouseID,
      paymentMethod,
      deadlineDelivery,
      internalNote,
      pickupWarehouseID,
      pickupReceiverID,
      expectedUpdatedAt: order?.updatedAt,
    })
      .then(async () => {
        notify.success({
          message: t("order.approvalOrder.success"),
        });
        await refetchOrder();
      })
      .catch((error) => {
        notify.error({
          message: t("order.approvalOrder.error"),
          description: error.message,
        });
      });

  const onChangeWarehouse = async (warehouseID) => {
    await handleChangeWarehouse({
      input: {
        orderID: id,
        warehouseID: warehouseID,
      },
    })
      .then(async () => {
        notify.success({
          message: t("v2.order.detail.modalChangeWarehouse.changeWarehouseSuccess"),
        });
        await refetchOrder();
      })
      .catch((error) => {
        notify.error({
          message: t("v2.order.detail.modalChangeWarehouse.changeWarehouseError"),
          description: error.message,
        });
      });
  };

  const dataToolBar = pick(order, ["shareLink", "seller", "createdByID", "saleChannel"]);

  const dataOrderCodeAndOrderStatus = pick(order, [
    "preOrder",
    "number",
    "ecommerceOrderSn",
    "status",
    "seller",
    "fullEvents",
    "shipperName",
    "shipperTelephone",
    "shippingType",
    "shippingStatus",
    "orderParcel",
    "saleChannel",
    "accountingNumber",
    "saleChannel",
    "orderType",
    "orderReconciliationStatus",
    "promotionType",
  ]);

  const dataInfoAgent = pick(order, [
    "seller",
    "orderType",
    "senderName",
    "senderPhone",
    "senderAddress",
    "receiverContactName",
    "receiverContactPhone",
    "receiverContactAddress",
    "shippingConfiguration",
    "shippingType",
    "shippingTypeName",
    "orderPriority",
    "exportedWarehouse",
    "deliveryDeadline",
    "chatURL",
    "fullStockRequired",
    "vat",
    "vatAllOrder",
    "vatTaxIDNumber",
    "vatCompanyName",
    "vatEmail",
    "vatAddress",
    "shippingFee",
    "shippingVersion",
    "customerVatEmail",
    "vatCustomerName",
    "customerVatIDNumber",
    "customerVatAddress",
    "orderReconciliationStatus",
    "status",
    "minDeliveryDate",
    "maxDeliveryDate",
  ]);
  const dataInfoProductAndBill = {
    ...pick(order, [
      "items",
      "status",
      "exportedWarehouseID",
      "orderType",
      "paymentMethod",
      "seller",
      "shippingFee",
      "operationFee",
      "totalServiceProduct",
      "discount",
      "loyaltyCashDiscount",
      "subTotal",
      "customerSubTotal",
      "customerTotal",
      "total",
      "note",
      "internalNote",
      "customerPaymentMethod",
      "totalPromotionDiscountMoney",
      "shippingType",
      "shippingConfiguration",
      "shippingStatus",
      "paymentStatus",
      "paymentInfo",
      "debtPayment",
      "exportedWarehouse",
      "customerShippingFee",
      "customerTotalServiceProduct",
      "sellerProfitAmount",
      "usedPoint",
      "depositInfo",
      "promotionType",
      "bankTransferAccounts",
    ]),
    isCanChangePaymentMethod: ASSIGN_UPDATE_ORDER_BY_STATUS?.[order?.status]?.changePaymentMethod,
    onApplyCashPaymentMethod,
    loadingApplyCashPaymentMethod,
  };

  const dataButtonGroup = {
    ...pick(order, [
      "preOrder",
      "status",
      "stockStatus",
      "shippingConfiguration",
      "shippingType",
      "items",
      "id",
      "exportedWarehouse",
      "number",
      "serviceFee",
      "discount",
      "shippingFee",
      "paymentMethod",
      "orderType",
      "shippingStatus",
      "shippingVersion",
      "deliveryDeadline",
      "chatURL",
      "seller",
      "messages",
      "potentialCustomer",
      "receiverContact",
      "sellerID",
      "sellerContractStatus",
      "contractSkipRemaining",
      "pickupInfo",
    ]),
    canApproveStock,
    canAssignShipper,
    canCancelOrder,
    canCompleteAssembly,
    canCompleteOrder,
    canConfirmPayment,
    canDenyStock,
    canExportStock,
    canRecreateCart,
    canWaitStock,
    canApproveOrder,
    canOrderCancelBeforePayView,
    canOrderCancelAfterPayView,
    loadingApproveOrder,
    canSuggestKeyLicenseAccountConfirm,
    canSuggestKeyLicenseAccountCancel,
  };

  const dataImageLibrary = pick(order, ["fileURLs"]);

  const dataCancellationReason = pick(order, ["cancelReason", "fullEvents"]);

  return {
    onExportOrder,
    onPrintOrder,
    dataToolBar,
    canViewOrder,
    loading:
      loadingExport ||
      loadingOrder ||
      loadingCancelOrder ||
      loadingWaitingStock ||
      loadingDenyStock ||
      loadingApproveStock ||
      loadingPackageOrder ||
      loadingCompleteAssembly ||
      loadingConfirmD3P ||
      loadingConfirmShipper ||
      loadingCloneCart ||
      loadingPermission ||
      loadingCloneCartUpdateDetail ||
      loadingApproveOrder ||
      loadingChangeWarehouse,
    dataOrderCodeAndOrderStatus,
    dataInfoAgent,
    dataInfoProductAndBill,
    dataButtonGroup,
    dataImageLibrary,
    dataCancellationReason,
    loadingPermission,
    order,
    refetchOrder,
    actions: {
      onCancelOrder,
      onWaitingForEnoughStock,
      onDeny,
      onApproveStock,
      onExportOrderWithPrint,
      onPackage,
      onExportConfirm,
      onAssemblyConfirm,
      onConfirmD3P,
      onConfirmShipper,
      onRecreateOrder,
      onEditOrder,
      onApproveOrder,
      onChangeWarehouse,
    },
  };
};

export default useDetail;
